import { useEffect, useRef, useState } from "react";

/** Plain toast hold (tokens.css `--toast-hold`); the handoff ScreenHost hides the toast after this. */
const TOAST_HOLD = 1800;

/** Toast shown by a screen script. `k` changes per toast so the in-animation replays. */
export interface ToastState {
  m: string;
  k: number;
}

export interface ScriptApi {
  /** Run `fn` after `ms`; every pending timer is cleared on unmount or when `play` turns false. */
  later: (fn: () => void, ms: number) => void;
  /** Show a toast for TOAST_HOLD ms. */
  toast: (message: string) => void;
}

/**
 * Runs a screen's scripted demo (handoff how-screens.html `setTimeout` chains) once `play` is true.
 * Nothing is scheduled while `play` is false, so the prerender and reduced-motion states stay at rest.
 * The phone remounts a screen (new React `key`) to replay its script — see HowPhone.
 */
export function useScript(play: boolean, script: (api: ScriptApi) => void): ToastState | null {
  const [toast, setToast] = useState<ToastState | null>(null);
  const scriptRef = useRef(script);
  useEffect(() => {
    scriptRef.current = script;
  });

  useEffect(() => {
    if (!play) return;
    const timers = new Set<number>();
    let seq = 0;
    const later = (fn: () => void, ms: number) => {
      const id = window.setTimeout(() => {
        timers.delete(id);
        fn();
      }, ms);
      timers.add(id);
    };
    const showToast = (m: string) => {
      const k = ++seq;
      setToast({ m, k });
      later(() => setToast((cur) => (cur && cur.k === k ? null : cur)), TOAST_HOLD);
    };
    scriptRef.current({ later, toast: showToast });
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      timers.clear();
    };
  }, [play]);

  return toast;
}
